'use client';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import type { FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { DesktopCarousel } from '../shared/desktop-carousel';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { ProductCard } from './product-card';
import { LandingPageHeading } from '../shared/landing-page-heading';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';

interface ProductsCarouselProps {
  products: Product[];
  locale?: string;
  query?: FetchProductQuery;
  title?: string;
  button: {
    href: string;
  };
  className?: {
    section?: string;
    container?: string;
  };
}

export function ProductsCarousel({
  products,
  locale,
  query,
  title,
  className,
  button,
}: ProductsCarouselProps) {
  const me = useCustomerStore(state => state.me);
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const { data } = useQuery({
    queryKey: [
      'LANDING_PAGE_PRODUCTS',
      locale,
      query,
      me?.customer.id ?? null,
      me?.authToken ?? null,
    ],
    queryFn: () => fetchProducts({ locale: locale!, query, authToken: me?.authToken }),
    enabled: isInitialized && Boolean(me?.authToken) && Boolean(locale) && Boolean(query),
  });
  const displayedProducts = data?.data ?? products;

  return (
    <>
      <CarouselProvider>
        <section
          id="top-produkty"
          className={cn(
            'custom-section',
            'px-0 py-16 sm:py-20 md:px-6 lg:py-24',
            className?.section,
          )}
        >
          <div
            className={cn(
              'custom-container',
              'flex flex-col items-start justify-start',
              className?.container,
            )}
          >
            <div className="flex w-full flex-col items-start justify-start gap-4 px-6 sm:gap-6 md:px-0 lg:gap-8 xl:gap-12">
              <LandingPageHeading badge="PRODUKTY" title="<span>TOP výber</span> produktov" />
              <div className="h-[1px] w-full bg-black/20" />
            </div>
            <div className="w-full">
              <DesktopCarousel
                responsive
                buttonHref={button.href}
                landingPageButton={true}
                className={{
                  divContainer: 'gap-12 md:gap-4',
                  slide: 'w-full max-w-[340px] sm:max-w-[400px] md:max-w-[397px]',
                  container: 'pl-6 pt-10 md:py-10 md:pl-0 lg:py-12',
                }}
                slides={displayedProducts.map(product => (
                  <ProductCard product={product} key={product.slug} />
                ))}
              />
            </div>
          </div>
        </section>
      </CarouselProvider>
    </>
  );
}
